// src/components/Layout.tsx
import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { 
  Home, 
  FileText, 
  Users, 
  Package, 
  Warehouse, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Truck,
  UserCog,
  PackagePlus,
  Settings,
  Shield,
  BarChart3
} from 'lucide-react'

// Menús según rol
const menuAdmin = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/notas-venta', label: 'Notas de Venta', icon: FileText },
  { path: '/clientes', label: 'Clientes', icon: Users },
  { path: '/productos', label: 'Productos', icon: Package },
  { path: '/inventario', label: 'Inventario', icon: Warehouse },
  { path: '/asignar-inventario', label: 'Asignar Inventario', icon: PackagePlus },
  { path: '/usuarios', label: 'Usuarios', icon: UserCog },
  { path: '/proveedores', label: 'Proveedores', icon: Truck },
  { path: '/estadisticas', label: 'Estadísticas', icon: BarChart3 },
  { path: '/configuracion', label: 'Configuración', icon: Settings },
  { path: '/verificar', label: 'Verificar Documento', icon: Shield },
]

const menuDistribuidor = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/notas-venta', label: 'Mis Ventas', icon: FileText },
  { path: '/notas-venta/nueva', label: 'Nueva Venta', icon: FileText },
  { path: '/mi-inventario', label: 'Mi Inventario', icon: Package },
  { path: '/clientes', label: 'Clientes', icon: Users },
  { path: '/verificar', label: 'Verificar Documento', icon: Shield },
]

const menuCliente = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/productos', label: 'Catálogo', icon: Package },
  { path: '/verificar', label: 'Verificar Documento', icon: Shield },
]

export function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  // Seleccionar menú según rol
  const menuItems = user?.rol === 'ADMINISTRADOR' 
    ? menuAdmin 
    : user?.rol === 'DISTRIBUIDOR' 
      ? menuDistribuidor 
      : menuCliente

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar móvil */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white">
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <span className="text-xl font-bold text-primary-600">BIO'DACAR</span>
            <button onClick={() => setSidebarOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r">
          <div className="flex items-center h-16 px-4 border-b">
            <span className="text-xl font-bold text-primary-600">BIO'DACAR</span>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b lg:px-8">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <div className="relative">
            <button
              className="flex items-center space-x-2 text-gray-700"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-medium">
                {user?.nombre?.charAt(0).toUpperCase()}
              </div>
              <span className="hidden md:block">{user?.nombre}</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1">
                <div className="px-4 py-2 border-b">
                  <p className="text-sm font-medium">{user?.nombre}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
                    {user?.rol}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
